import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { subscribeToProjects, createProject, subscribeToNotifications } from '../services/firebaseService';
import CreateProjectModal from '../components/CreateProjectModal';
import NotificationsTab from '../components/NotificationsTab';

const Dashboard = () => {
  const [searchText, setSearchText] = useState('');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState('projects');
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, isAdmin } = useAuth();

  useEffect(() => {
    if (!currentUser) return;

    const unsubscribeProjects = subscribeToProjects(currentUser.uid, (projectsData) => {
      setProjects(projectsData);
      setLoading(false);
    });

    const unsubscribeNotifications = subscribeToNotifications(currentUser.uid, (notificationsData) => {
      // Notifications are handled in Layout component
    });

    return () => {
      unsubscribeProjects();
      unsubscribeNotifications();
    };
  }, [currentUser]);

  const handleProjectClick = (projectId) => {
    navigate(`/project/${projectId}`);
  };

  const handleCreateProject = async (projectData) => {
    try {
      await createProject(projectData, currentUser.uid);
      setShowCreateModal(false);
    } catch (error) {
      console.error('Error creating project:', error);
    }
  };

  const filteredProjects = projects.filter(project =>
    project.name?.toLowerCase().includes(searchText.toLowerCase()) ||
    project.location?.toLowerCase().includes(searchText.toLowerCase())
  );

  // Handle URL parameters for tab selection
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    if (tab === 'notifications') {
      setActiveTab('notifications');
    } else {
      setActiveTab('projects');
    }
  }, [location.search]);

  return (
    <>
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-semibold text-gray-900">
              {activeTab === 'projects' ? 'Projects' : 'Notifications'}
            </h1>
            {activeTab === 'projects' && isAdmin() && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Create Project</span>
              </button>
            )}
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search projects..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-80 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 p-6">
        {activeTab === 'projects' ? (
          loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProjects.length > 0 ? (
                  filteredProjects.map((project) => (
                    <div
                      key={project.id}
                      onClick={() => handleProjectClick(project.id)}
                      className="bg-gradient-to-br from-purple-100 via-pink-50 to-orange-100 rounded-3xl shadow-lg p-8 cursor-pointer hover:shadow-xl transition-all duration-300 hover:scale-[1.02] relative overflow-hidden min-h-[280px] flex flex-col justify-between"
                    >
                      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/30 to-transparent pointer-events-none"></div>
                      
                      <div className="relative z-10 flex flex-col h-full">
                        <div className="flex items-center justify-start mb-8">
                          <span className="inline-flex items-center px-5 py-2.5 rounded-full text-sm font-semibold bg-gradient-to-r from-orange-400 to-orange-500 text-white shadow-lg">
                            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            {project.status || 'On Track'}
                          </span>
                        </div>
                        
                        <div className="flex-1 flex flex-col justify-center text-right mb-8">
                          <h3 className="text-3xl font-bold text-gray-800 mb-3 leading-tight">
                            {project.name}
                          </h3>
                          
                          <p className="text-gray-700 font-medium text-xl">
                            {project.location}
                          </p>
                        </div>
                        
                        <div className="text-right text-sm text-gray-600">
                          <span className="font-medium">Start Date: {project.startDate || new Date(project.createdAt?.toDate()).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-full text-center py-12">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 00-2 2m0 0V5a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2m-2-4h.01M17 16h.01" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No projects</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {isAdmin() ? 'Get started by creating a new project.' : 'Contact an administrator to create projects.'}
                    </p>
                    {isAdmin() && (
                      <div className="mt-6">
                        <button
                          onClick={() => setShowCreateModal(true)}
                          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        >
                          <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          New Project
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          ) : (
            <NotificationsTab 
              onNavigateToTask={(projectId, activityId, taskId, openChat = false) => {
                if (openChat) {
                  navigate(`/project/${projectId}?activity=${activityId}&task=${taskId}&chat=true`);
                } else {
                  navigate(`/project/${projectId}?activity=${activityId}&task=${taskId}`);
                }
              }}
              onNavigateToActivity={(projectId, activityId) => {
                navigate(`/project/${projectId}?activity=${activityId}`);
              }}
              onNavigateToProject={(projectId) => {
                navigate(`/project/${projectId}`);
              }}
            />
          )}
        </main>

      {/* Create Project Modal */}
      {showCreateModal && <CreateProjectModal onClose={() => setShowCreateModal(false)} onSubmit={handleCreateProject} />}
    </>
  );
};

export default Dashboard;
